import { FC, useContext, useEffect, useState } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router';

import type { AgentCorePageContext } from '@/shared/api/agentCore/agentCoreApi';
import api from '@/shared/api/api';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector } from '@/shared/hooks';
import { CHAT_URL_PARAMS } from '@/shared/lib';

import { organizationModel } from '@/entities/organization';
import { userModel } from '@/entities/user';

import {
  PatientAgentChat,
  AgentChatHostContext,
  resolveAgentPageContext,
} from '@/widgets/AgentChat';
import { ChatThreadList } from '@/widgets/ChatThreadList';

type RoutedAgentChatProps = {
  pageContext: AgentCorePageContext;
  patientID: string;
};

const RoutedAgentChat: FC<RoutedAgentChatProps> = ({
  pageContext,
  patientID,
}) => {
  const navigate = useNavigate();
  const { host } = useContext(AgentChatHostContext);
  // Runtime mirror of the widget's active thread for the sidebar; the
  // selected thread itself persists in localStorage (agentChatPreferences),
  // not in the URL.
  const [threadID, setThreadID] = useState('');

  return (
    <PatientAgentChat
      patientID={patientID}
      floating
      {...host}
      defaultGeneral={!patientID}
      onOpenDocument={async (documentID) => {
        const { Document } = await api.patientDocument.getPatientDocument({
          DocumentID: documentID,
        });
        if (Document?.PatientID)
          navigate(
            `/patients/${encodeURIComponent(Document.PatientID)}/documents/${encodeURIComponent(documentID)}`,
          );
      }}
      initialThreadID={threadID}
      pageContext={pageContext}
      onThreadChanged={setThreadID}
      sidebarContent={
        <ChatThreadList
          patientID={patientID}
          threadID={threadID}
          showSearch={false}
          subject={pageContext.subject ?? null}
          onThreadOpen={setThreadID}
        />
      }
    />
  );
};

export const AgentChatRoutes: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Chat state persists in localStorage; strip legacy chat parameters
  // (?thread / ?chat / ?chatPinned) once so old URLs stop carrying them.
  useEffect(() => {
    const current = new URLSearchParams(location.search);
    if (!CHAT_URL_PARAMS.some((key) => current.has(key))) {
      return;
    }
    for (const key of CHAT_URL_PARAMS) {
      current.delete(key);
    }
    navigate({ search: current.toString() }, { replace: true });
    // Run once per mount: the stripped URL cannot re-acquire these params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const patientRoute = useMatch('/patients/:patientID/*');
  const enabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_AgentChat,
    ),
  );
  const currentUserID = useAppSelector(userModel.selectors.selectCurrentUserID);
  const organizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const patientID = patientRoute?.params.patientID ?? '';
  // Page + focused resources for the current URL; see lib/pageContext for
  // the full route table.
  const pageContext = resolveAgentPageContext(
    location.pathname,
    location.search,
  );

  // One host persists across patient, document, and general navigation.
  if (!enabled) {
    return null;
  }

  return (
    <RoutedAgentChat
      key={`${currentUserID}:${organizationID}`}
      pageContext={pageContext}
      patientID={patientID}
    />
  );
};
